import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  TextField,
  Button,
  Stack,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Visibility, Download } from '@mui/icons-material';
import auditApi from '../../api/auditApi';
import AuditDetailModal from './AuditDetailModal';
import BackButtonDesktop from '../../components/BackButtonDesktop';

export default function AuditListPage() {
  const [audits, setAudits] = useState([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('DESC');

  const [filters, setFilters] = useState({});

  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const roleOptions = ['ADMIN','CONTRIBUENTE','STUDIO'];
  const entityOptions = ['AUDIT','CONTRATTO','CONTRIBUENTE','DOCUMENTO','SCADENZA','STUDIO','TIPOCONTRATTO','USER'];
  const outcomeOptions = ['SUCCESS','ERROR'];
  const actionOptions = ['LOGIN','CREATE','UPDATE','DELETE','READ','UPLOAD_PDF','DOWNLOAD_PDF'];

  useEffect(() => {
    fetchAudits();
  }, [page, pageSize, sortField, sortDirection]);

  const fetchAudits = async () => {
    try {
      const payload = { ...filters };
      if (payload.from) payload.from = new Date(payload.from).toISOString();
      if (payload.to) payload.to = new Date(payload.to).toISOString();

      const res = await auditApi.search(payload, page, pageSize, `${sortField},${sortDirection}`);
      setAudits(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      console.error('Errore caricamento audit:', err);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'ASC' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(0);
    fetchAudits();
  };

  const clearFilters = () => {
    setFilters({});
    setPage(0);
    fetchAudits();
  };

  const exportToCSV = async () => {
    try {
      const payload = { ...filters };
      if (payload.from) payload.from = new Date(payload.from).toISOString();
      if (payload.to) payload.to = new Date(payload.to).toISOString();

      const initialRes = await auditApi.search(payload, 0, 1, `${sortField},${sortDirection}`);
      const totalRecords = initialRes.data.totalElements;
      if (!totalRecords) return;

      const res = await auditApi.search(payload, 0, totalRecords, `${sortField},${sortDirection}`);
      const allAudits = res.data.content;

      if (!allAudits || !allAudits.length) return;

      const headers = Object.keys(allAudits[0]);
      const rows = allAudits.map(a => headers.map(h => a[h] ?? ''));

      let csvContent = "data:text/csv;charset=utf-8,"
        + [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "audit_log_full.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Errore esportazione CSV:', err);
    }
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 2 }}>Audit Log</Typography>

      {/* Filtri */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Username" name="username" value={filters.username || ''} onChange={handleFilterChange} size="small"/>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Role</InputLabel>
          <Select
            name="role"
            value={filters.role || ''}
            onChange={handleFilterChange}
            label="Role"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {roleOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Action</InputLabel>
          <Select
            name="action"
            value={filters.action || ''}
            onChange={handleFilterChange}
            label="Action"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {actionOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Entity</InputLabel>
          <Select
            name="entity"
            value={filters.entity || ''}
            onChange={handleFilterChange}
            label="Entity"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {entityOptions.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Outcome</InputLabel>
          <Select
            name="outcome"
            value={filters.outcome || ''}
            onChange={handleFilterChange}
            label="Outcome"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {outcomeOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          label="From"
          name="from"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.from || ''}
          onChange={handleFilterChange}
          size="small"
        />
        <TextField
          label="To"
          name="to"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.to || ''}
          onChange={handleFilterChange}
          size="small"
        />
        <Button variant="contained" onClick={applyFilters}>Filtra</Button>
        <Button variant="outlined" onClick={clearFilters}>Clear All</Button>
        <Button variant="outlined" startIcon={<Download />} onClick={exportToCSV}>Esporta CSV</Button>
      </Stack>

      {/* Tabella */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell onClick={() => handleSort('entity')} sx={{ cursor: 'pointer' }}>
              Entity {renderSortIcon('entity')}
            </TableCell>
            <TableCell onClick={() => handleSort('action')} sx={{ cursor: 'pointer' }}>
              Action {renderSortIcon('action')}
            </TableCell>
            <TableCell onClick={() => handleSort('username')} sx={{ cursor: 'pointer' }}>
              Username {renderSortIcon('username')}
            </TableCell>
            <TableCell sx={{ cursor: 'pointer' }}>
                 Role
            </TableCell>
            <TableCell onClick={() => handleSort('timestamp')} sx={{ cursor: 'pointer' }}>
              Timestamp {renderSortIcon('timestamp')}
            </TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Azioni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {audits.map(audit => (
            <TableRow key={audit.id}>
              <TableCell>{audit.entity}</TableCell>
              <TableCell>{audit.action}</TableCell>
              <TableCell>{audit.username}</TableCell>
              <TableCell>{audit.role}</TableCell>
              <TableCell>{new Date(audit.timestamp).toLocaleString()}</TableCell>
              <TableCell>{audit.outcome}</TableCell>
              <TableCell>
                <IconButton color="primary" size="small" onClick={() => { setSelectedAudit(audit); setDetailOpen(true); }}>
                  <Visibility />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableRow>
        </TableFooter>
      </Table>

      {selectedAudit && (
        <AuditDetailModal
          open={detailOpen}
          audit={selectedAudit}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </Container>
  );
}